import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Stack,
  Group,
  Text,
  Paper,
  Badge,
  Button,
  Skeleton,
  SimpleGrid,
  Alert,
  Card,
  ActionIcon,
  Menu,
  Anchor,
  Table,
  Divider,
} from "@mantine/core";
import {
  IconGift,
  IconAlertCircle,
  IconHistory,
  IconPlus,
  IconMinus,
  IconBan,
  IconDotsVertical,
  IconLink,
  IconPencil,
  IconPrinter,
} from "@tabler/icons-react";
import moment from "moment";
import { useApp } from "../../context/AppContext";
import { useSalon } from "../../context/SalonContext";
import { useSalonScopedFetch } from "../../hooks/useSalonScopedFetch";
import usePermissions from "../../hooks/usePermissions";
import { BusinessInfo, SalonContextType } from "../../types/context";
import { GiftCard, GiftCardLedgerEntry } from "../../types/giftCards";
import { formatCurrency } from "../../utils/cashDrawerUtils";
import { giftCardStatusColor } from "../../utils/giftCardStatus";
import AddGiftCardModal from "./AddGiftCardModal";
import AttachGiftCardModal from "./AttachGiftCardModal";
import AdjustGiftCardModal, { AdjustGiftCardMode } from "./AdjustGiftCardModal";
import VoidGiftCardModal from "./VoidGiftCardModal";
import EditGiftCardCodeModal from "./EditGiftCardCodeModal";
import GiftCardHistoryModal from "./GiftCardHistoryModal";
import useGiftCardPrint from "../common/useGiftCardPrint";
import { giftCardRecipientName } from "../../utils/giftCardRecipient";
import styles from "./ClientGiftCardsSection.module.css";

interface ClientGiftCardsSectionProps {
  clientId: string;
  sectionTitle?: string;
  sectionSubtitle?: string;
}

/** Void is blocked server-side once a card is terminal (already voided). */
const isTerminalStatus = (status: string): boolean =>
  ["voided"].includes(status.toLowerCase());

/** Balance adjustments are blocked on terminal cards AND once expired. */
const isAdjustableStatus = (status: string): boolean =>
  !isTerminalStatus(status) && status.toLowerCase() !== "expired";

/** Adjust-modal target: which card and in which direction (grant vs detract). */
interface AdjustTarget {
  card: GiftCard;
  mode: AdjustGiftCardMode;
}

interface GiftCardCardProps {
  card: GiftCard;
  salonId: string;
  getLedger: (
    salonId: string,
    giftCardId: string,
  ) => Promise<GiftCardLedgerEntry[]>;
  /** True when the viewer may grant/detract value (permission-gated). */
  canManage: boolean;
  /** True when the viewer may void a card (Owner/Manager only). */
  canVoid: boolean;
  onAdjust: (target: AdjustTarget) => void;
  onEditCode: (card: GiftCard) => void;
  onVoid: (card: GiftCard) => void;
  /** Print a certificate for this card (single shared print mount at the section). */
  onPrint: (card: GiftCard) => void;
  /** Bumped after any successful action to reload an already-loaded history. */
  historyToken: number;
}

/** One held gift card: balance + status + an actions menu (history in a modal). */
function GiftCardCard({
  card,
  salonId,
  getLedger,
  canManage,
  canVoid,
  onAdjust,
  onEditCode,
  onVoid,
  onPrint,
  historyToken,
}: GiftCardCardProps) {
  const [historyOpen, setHistoryOpen] = useState(false);

  const showAdjustActions = canManage && isAdjustableStatus(card.status);
  const showVoidAction = canVoid && !isTerminalStatus(card.status);
  const isGift =
    !!card.purchaser_client_uuid &&
    card.purchaser_client_uuid !== card.client_uuid;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={4}>
            <Group gap="xs" align="center" wrap="nowrap">
              <Text fw={600}>{card.code}</Text>
              <Badge
                size="sm"
                variant="light"
                color={giftCardStatusColor(card.status)}
              >
                {card.status}
              </Badge>
            </Group>
            {card.expires_at && (
              <Text size="xs" c="dimmed">
                Expires {moment(card.expires_at).format("MMM D, YYYY")}
              </Text>
            )}
            {isGift && card.purchaser_name && (
              <Text size="xs" c="dimmed">
                Purchased by{" "}
                <Anchor
                  component={Link}
                  to={`/admin/clients/${card.purchaser_client_uuid}`}
                  size="xs"
                >
                  {card.purchaser_name}
                </Anchor>
              </Text>
            )}
          </Stack>
          <Menu position="bottom-end" shadow="md" withinPortal>
            <Menu.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label={`Actions for ${card.code}`}
              >
                <IconDotsVertical size={18} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconHistory size={16} />}
                onClick={() => setHistoryOpen(true)}
              >
                View history
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPrinter size={16} />}
                onClick={() => onPrint(card)}
              >
                Print gift card
              </Menu.Item>
              {canManage && (
                <Menu.Item
                  leftSection={<IconPencil size={16} />}
                  onClick={() => onEditCode(card)}
                >
                  Edit code
                </Menu.Item>
              )}
              {showAdjustActions && (
                <Menu.Item
                  leftSection={<IconPlus size={16} />}
                  onClick={() => onAdjust({ card, mode: "grant" })}
                >
                  Grant value
                </Menu.Item>
              )}
              {showAdjustActions && (
                <Menu.Item
                  leftSection={<IconMinus size={16} />}
                  onClick={() => onAdjust({ card, mode: "detract" })}
                >
                  Detract value
                </Menu.Item>
              )}
              {showVoidAction && (
                <Menu.Item
                  color="red"
                  leftSection={<IconBan size={16} />}
                  onClick={() => onVoid(card)}
                >
                  Void
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>

        <Group justify="space-between" align="baseline">
          <Text size="sm" c="dimmed">
            Balance
          </Text>
          <Text fw={600} c={card.balance > 0 ? "var(--brand-teal)" : "dimmed"}>
            {formatCurrency(card.balance)}
          </Text>
        </Group>
      </Stack>

      <GiftCardHistoryModal
        opened={historyOpen}
        onClose={() => setHistoryOpen(false)}
        salonId={salonId}
        giftCardId={card.id}
        code={card.code}
        getLedger={getLedger}
        historyToken={historyToken}
      />
    </Card>
  );
}

/** Read-only list of cards this client purchased, linking to each holder. */
function PurchasedGiftCards({ cards }: { cards: GiftCard[] }) {
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Text fw={500}>Purchased</Text>
        <Badge size="sm" variant="light" color="gray">
          {cards.length}
        </Badge>
      </Group>
      <Text size="xs" c="dimmed">
        Gift cards this client bought for someone else.
      </Text>
      <Table.ScrollContainer minWidth={420}>
        <Table verticalSpacing="xs" fz="sm" withRowBorders={false}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Code</Table.Th>
              <Table.Th ta="right">Balance</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Held by</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {cards.map((card) => (
              <Table.Tr key={card.id}>
                <Table.Td>{card.code}</Table.Td>
                <Table.Td ta="right">{formatCurrency(card.balance)}</Table.Td>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={giftCardStatusColor(card.status)}
                  >
                    {card.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {card.client_uuid ? (
                    <Anchor
                      component={Link}
                      to={`/admin/clients/${card.client_uuid}`}
                      size="sm"
                    >
                      {card.client_name ?? "View holder"}
                    </Anchor>
                  ) : (
                    <Text size="sm" c="dimmed">
                      Unclaimed
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Stack>
  );
}

export default function ClientGiftCardsSection({
  clientId,
  sectionTitle,
  sectionSubtitle,
}: ClientGiftCardsSectionProps) {
  const { getClientGiftCards, getGiftCardLedger } = useApp();
  const { businessInfo } = useSalon() as SalonContextType;
  const salonId = (businessInfo as BusinessInfo | null)?.id ?? "";
  const perms = usePermissions();
  const canManage = perms.can("giftCards", "canManageClientGiftCards");
  const canVoid = perms.can("giftCards", "canVoidGiftCards");
  const { printCard, certificate } = useGiftCardPrint();

  // Print a certificate for one held card. Hoisted to the section (not the card)
  // so exactly one hidden print copy is ever mounted across the whole grid — the
  // @media print rule reveals every mounted `.printOnly`, so a per-card mount
  // would print all of them at once. This client is the holder ("To").
  const printCert = useCallback(
    (card: GiftCard) =>
      printCard({
        code: card.code,
        initialAmount: card.initial_amount,
        balance: card.balance,
        status: card.status,
        purchaserName: card.purchaser_name,
        // Prefer the recipient recorded at sale time; fall back to the holder.
        recipientName:
          giftCardRecipientName(
            card.recipient_first_name,
            card.recipient_last_name,
          ) ?? card.client_name,
        giftMessage: card.gift_message,
        expiresAt: card.expires_at,
        purchasedAt: card.purchased_dt,
      }),
    [printCard],
  );

  const [held, setHeld] = useState<GiftCard[]>([]);
  const [purchased, setPurchased] = useState<GiftCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Action-modal targets (null = closed). Add has no target beyond the client.
  const [addOpen, setAddOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<AdjustTarget | null>(null);
  const [editCodeTarget, setEditCodeTarget] = useState<GiftCard | null>(null);
  const [voidTarget, setVoidTarget] = useState<GiftCard | null>(null);
  // Bumped after any successful action to reload already-loaded histories.
  const [historyToken, setHistoryToken] = useState(0);

  const heldSorted = useMemo(() => {
    // Soonest-upcoming-expiry first; never-expires (null) sorts to the bottom.
    return [...held].sort((a, b) => {
      if (!a.expires_at) return 1;
      if (!b.expires_at) return -1;
      return Date.parse(a.expires_at) - Date.parse(b.expires_at);
    });
  }, [held]);

  const fetchGiftCards = useCallback(async () => {
    if (!clientId || !salonId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const { held: heldCards, purchased: purchasedCards } =
        await getClientGiftCards(salonId, clientId);
      setHeld(heldCards);
      setPurchased(purchasedCards);
    } catch (err) {
      console.error("Error fetching client gift cards:", err);
      setError("Failed to load gift cards.");
    } finally {
      setIsLoading(false);
    }
  }, [clientId, salonId, getClientGiftCards]);

  // Without ids there is nothing to fetch — clear the initial spinner.
  useEffect(() => {
    if (!clientId || !salonId) setIsLoading(false);
  }, [clientId, salonId]);

  // Fetch ONCE per (salon, client) — mirrors the packages section's guard so
  // upstream identity churn can't refire the fetch unbounded on a 403.
  const { retry: retryGiftCards } = useSalonScopedFetch(
    salonId && clientId ? `${salonId}:${clientId}` : null,
    fetchGiftCards,
    { tag: "client-gift-cards" },
  );

  // After any successful action: refetch and signal loaded histories to reload.
  const handleActionSuccess = useCallback(() => {
    retryGiftCards();
    setHistoryToken((token) => token + 1);
  }, [retryGiftCards]);

  const renderHeadingRow = () => (
    <Group
      justify="space-between"
      align="flex-start"
      wrap="wrap"
      gap="md"
      className={sectionTitle ? styles.sectionHead : undefined}
    >
      {sectionTitle ? (
        <Stack gap={4}>
          <Text fw={600} size="lg" c="dark.8">
            {sectionTitle}
          </Text>
          {sectionSubtitle ? (
            <Text size="sm" c="dimmed">
              {sectionSubtitle}
            </Text>
          ) : null}
        </Stack>
      ) : (
        <Group gap="xs">
          <IconGift size={18} />
          <Text fw={500}>Gift Cards</Text>
          {held.length > 0 && (
            <Badge size="sm" variant="light" color="gray">
              {held.length}
            </Badge>
          )}
        </Group>
      )}
      {canManage && (
        <Group gap="xs">
          <Button
            variant="light"
            color="var(--brand-teal)"
            leftSection={<IconLink size={16} />}
            onClick={() => setAttachOpen(true)}
          >
            Attach existing
          </Button>
          <Button
            color="var(--brand-teal)"
            leftSection={<IconPlus size={16} />}
            onClick={() => setAddOpen(true)}
          >
            Add gift card
          </Button>
        </Group>
      )}
    </Group>
  );

  if (isLoading) {
    return (
      <Stack gap="xs">
        {renderHeadingRow()}
        <Skeleton height={80} radius="md" />
        <Skeleton height={80} radius="md" />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {renderHeadingRow()}

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setError(null)}
        >
          <Group justify="space-between" align="center">
            <Text size="sm">{error}</Text>
            <Button
              size="compact-xs"
              variant="light"
              color="red"
              onClick={retryGiftCards}
            >
              Retry
            </Button>
          </Group>
        </Alert>
      )}

      {held.length === 0 ? (
        <Paper p="md" withBorder className={styles.emptyState}>
          <Stack align="center" gap="xs">
            <IconGift size={32} color="var(--mantine-color-gray-5)" />
            <Text size="sm" c="dimmed" ta="center">
              No gift cards held
            </Text>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {heldSorted.map((card) => (
            <GiftCardCard
              key={card.id}
              card={card}
              salonId={salonId}
              getLedger={getGiftCardLedger}
              canManage={canManage}
              canVoid={canVoid}
              onAdjust={setAdjustTarget}
              onEditCode={setEditCodeTarget}
              onVoid={setVoidTarget}
              onPrint={printCert}
              historyToken={historyToken}
            />
          ))}
        </SimpleGrid>
      )}

      {purchased.length > 0 && (
        <>
          <Divider />
          <PurchasedGiftCards cards={purchased} />
        </>
      )}

      {canManage && (
        <AddGiftCardModal
          opened={addOpen}
          onClose={() => setAddOpen(false)}
          salonId={salonId}
          clientId={clientId}
          onSuccess={handleActionSuccess}
        />
      )}

      {canManage && (
        <AttachGiftCardModal
          opened={attachOpen}
          onClose={() => setAttachOpen(false)}
          salonId={salonId}
          clientId={clientId}
          onSuccess={handleActionSuccess}
        />
      )}

      {adjustTarget && (
        <AdjustGiftCardModal
          opened
          onClose={() => setAdjustTarget(null)}
          salonId={salonId}
          giftCardId={adjustTarget.card.id}
          mode={adjustTarget.mode}
          balance={adjustTarget.card.balance}
          onSuccess={handleActionSuccess}
        />
      )}

      {editCodeTarget && (
        <EditGiftCardCodeModal
          opened
          onClose={() => setEditCodeTarget(null)}
          salonId={salonId}
          giftCardId={editCodeTarget.id}
          currentCode={editCodeTarget.code}
          onSuccess={handleActionSuccess}
        />
      )}

      {voidTarget && (
        <VoidGiftCardModal
          opened
          onClose={() => setVoidTarget(null)}
          salonId={salonId}
          giftCardId={voidTarget.id}
          code={voidTarget.code}
          balance={voidTarget.balance}
          onSuccess={handleActionSuccess}
        />
      )}

      {certificate}
    </Stack>
  );
}
